import pb from '../pocketbase';

const GENDER_CACHE_KEY = 'sense.gender';

function cacheGender(gender) {
    try {
        localStorage.setItem(GENDER_CACHE_KEY, gender);
    } catch (e) {
        console.warn('Failed to cache gender:', e);
    }
}

export function getCachedGender() {
    try {
        const cached = localStorage.getItem(GENDER_CACHE_KEY);
        return cached === 'male' ? 'male' : 'female';
    } catch (e) {
        return 'female';
    }
}

/**
 * Определяет и фиксирует пол пользователя:
 * - если в profiles.gender уже male/female — кладёт в кеш и возвращает
 * - если unknown — вызывает edge-функцию detect-gender, маппит результат и обновляет БД
 * Возвращает 'male' | 'female' или null, если пользователя нет/ошибка.
 */
export async function ensureUserGender() {
    try {
        console.log('[ensureUserGender] 🚀 Starting gender detection...');

        const user = pb.authStore.model;

        if (!user) {
            console.error('[ensureUserGender] ❌ no user');
            return null;
        }

        const userId = user.id;
        console.log('[ensureUserGender] ✅ User ID:', userId);
        console.log('[ensureUserGender] 📋 User data:', user);

        // 2. Профиль - для PocketBase берём данные из user record
        const profile = user;

        console.log('[ensureUserGender] 📄 Profile loaded:', profile);

        let gender = profile.gender || 'unknown';
        console.log('[ensureUserGender] 🔍 Current gender in DB:', gender);

        // 3. Если уже male/female — просто кладём в кеш и выходим
        if (gender === 'male' || gender === 'female') {
            console.log('[ensureUserGender] ✅ Gender already set, caching:', gender);
            cacheGender(gender);
            return gender;
        }

        // 4. gender = unknown → пробуем автоопределить
        console.log('[ensureUserGender] 🔄 Gender is unknown, attempting auto-detection...');

        const fullNameFromProfile = profile.full_name || '';
        const fullNameFromMeta = user.full_name || user.username || '';

        const fullName = fullNameFromProfile || fullNameFromMeta;
        const firstName = fullName.trim().split(/\s+/)[0];

        console.log('[ensureUserGender] 👤 Full name from profile:', fullNameFromProfile);
        console.log('[ensureUserGender] 👤 Full name from metadata:', fullNameFromMeta);
        console.log('[ensureUserGender] 👤 Extracted first name:', firstName);

        if (!firstName) {
            console.log('[ensureUserGender] ⚠️ No name found, using fallback: female');
            const fallback = 'female';
            cacheGender(fallback);
            try {
                await pb.collection('users').update(userId, { gender: fallback });
                console.log('[ensureUserGender] ✅ Gender updated to fallback: female');
            } catch (updateError) {
                console.error('[ensureUserGender] ❌ Failed to update gender (fallback):', updateError);
            }
            return fallback;
        }

        // 5. Просто используем fallback female (edge function detect-gender не реализована в PocketBase)
        console.log('[ensureUserGender] 🔄 Using default gender: female');
        const normalized = 'female';

        // 6. обновляем в БД
        console.log('[ensureUserGender] 💾 Updating database with gender:', normalized);
        try {
            await pb.collection('users').update(userId, { gender: normalized });
            console.log('[ensureUserGender] ✅ Gender successfully updated in DB');
        } catch (updateError) {
            console.error('[ensureUserGender] ❌ update gender error', updateError);
        }

        // 7. кешируем и возвращаем
        cacheGender(normalized);
        console.log('[ensureUserGender] 💾 Gender cached in localStorage:', normalized);
        console.log('[ensureUserGender] 🎉 Final result:', normalized);

        return normalized;

    } catch (err) {
        console.error('[ensureUserGender] ❌ unexpected error', err);
        return null;
    }
}

/**
 * Применяет гендерные формы к тексту.
 * Заменяет паттерны вида (мужская форма/женская форма) на соответствующую форму.
 * Если gender не 'male' (в т.ч. 'unknown', null, undefined), используется женская форма.
 * 
 * @param {string} text - Текст с паттернами, например: "Сегодня (он/она) приш(ел/ла) домой"
 * @param {('male'|'female'|'unknown'|null)} gender - Пол пользователя
 * @returns {string} - Текст с применёнными гендерными формами
 * 
 * @example
 * applyGender("Сегодня (он/она) приш(ел/ла) домой", "male")
 * // "Сегодня он пришел домой"
 * 
 * applyGender("Сегодня (он/она) приш(ел/ла) домой", "female")
 * // "Сегодня она пришла домой"
 * 
 * applyGender("Сегодня (он/она) приш(ел/ла) домой", "unknown")
 * // "Сегодня она пришла домой"
 */
export function applyGender(text, gender) {
    // Handle undefined/null text
    if (!text) return '';

    // Если пол явно не указан или unknown, считаем female
    const isMale = gender === 'male';

    return text.replace(/\(([^/()]+)\/([^()]+)\)/g, (_, male, female) =>
        isMale ? male : female
    );
}
